// src/components/CreateCrewModal.jsx
import React, { useState } from 'react';
import { X, Users } from 'lucide-react';
import { supabase } from '../supabaseClient';

export default function CreateCrewModal({ onClose, onSuccess, currentUserId, onRequireAuth }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('👥');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);

  const EMOJIS = ['👥','🔥','👑','⚡','💀','🎮','🏆','⚔️','🎯','💎','🌟','🦅'];

  async function handleCreate(e) {
  e.preventDefault();
  if (!name.trim() || name.length < 3) {
    alert('Crew name must be at least 3 characters');
    return;
  }

  setSaving(true);
  try {
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (!currentUserId || !session?.user) {
      if (typeof onRequireAuth === 'function') {
        onRequireAuth();
      } else {
        alert('Enter your email and we’ll send a 6-digit code to start your crew.');
      }
      return;
    }

    // Create crew with member_count = 0 initially
    const { data: crew, error: crewError } = await supabase
      .from('crews')
      .insert({
        name: name.trim(),
        emoji,
        description: description.trim() || null,
        created_by: currentUserId,
        is_private: isPrivate,
      })
      .select()
      .single();

    if (crewError) {
      console.error('Crew creation error:', crewError);
      throw crewError;
    }

    console.log('✅ Crew created:', crew.id);

    // Auto-add creator as first member
    const { error: memberError } = await supabase
      .from('crew_members')
.upsert(
  {
    crew_id: crew.id,
    user_id: currentUserId,
    role: 'creator',
  },
  { onConflict: 'crew_id,user_id' }
);

    if (memberError) {
      console.error('Member add error:', memberError);
      throw memberError;
    }

    console.log('✅ Creator added as member');

    console.log('✅ Member count updated');

    // 📊 Track crew creation
    if (window.trackEvent) {
      window.trackEvent('crew_created', {
        crew_id: crew.id,
        crew_name: crew.name,
        is_private: isPrivate
      });
    }

    // Success!
    alert(`🎉 Crew "${crew.name}" created!

Your crew is ready to summon! Find it in:
- Jury Summons on any trial
- Your profile page

Invite friends to join and start voting together!`);

    onSuccess(crew);
    onClose();
  } catch (err) {
    console.error('Error creating crew:', err);
    alert(`Could not create crew: ${err.message}`);
  } finally {
    setSaving(false);
  }
}

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-black text-white">Create Crew</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleCreate} className="p-6 space-y-5">
          {/* Crew Name */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">
              Crew Name *
            </label>
            <input
              className="w-full bg-slate-900 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-500"
              placeholder="e.g., The Legends, Night Owls, Debate Club"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              required
              autoFocus
            />
            <div className="text-xs text-slate-400 mt-1">
              {name.length}/50 characters (min 3)
            </div>
          </div>

          {/* Crew Emoji */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">
              Crew Emoji
            </label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map(e => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEmoji(e)}
                  className={`px-4 py-3 rounded-xl border-2 transition-all ${
                    emoji === e 
                      ? 'bg-purple-600 border-purple-500 scale-110 shadow-lg' 
                      : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <span className="text-3xl">{e}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">
              Description (optional)
            </label>
            <textarea
              className="w-full bg-slate-900 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-500 resize-none"
              placeholder="What's your crew about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={200}
            />
            <div className="text-xs text-slate-400 mt-1">
              {description.length}/200 characters
            </div>
          </div>

          {/* Privacy */}
          <div className="flex items-start gap-3 p-4 bg-slate-900 rounded-xl">
            <input
              type="checkbox"
              id="isPrivate"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-slate-600 text-purple-600 focus:ring-purple-500 bg-slate-800"
            />
            <label htmlFor="isPrivate" className="flex-1 cursor-pointer">
              <div className="text-sm font-semibold text-white">Private Crew</div>
              <div className="text-xs text-slate-400 mt-1">
                Only members can see this crew. New members need an invite code.
              </div>
            </label>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-slate-900 rounded-xl border border-slate-700">
            <div className="text-sm text-slate-300">
              <div className="font-bold text-white mb-2">🎯 Your crew can:</div>
              <ul className="space-y-1 list-disc list-inside text-xs">
                <li>Vote first on your trials (guaranteed 12 votes!)</li>
                <li>See crew-only leaderboards and stats</li>
                <li>Challenge other crews to debates</li>
                <li>Grow to 1,000 members</li>
              </ul>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl px-4 py-3 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || name.length < 3}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-black text-lg rounded-xl px-4 py-3 transition-all transform hover:scale-105"
            >
              {saving ? 'Creating...' : '🎯 Create Crew'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
